// v1.0.0 - 2026-01-27 - Sidebar navigation component

import { NavLink } from 'react-router-dom'
import { cn } from '@/lib/utils/cn'
import {
  LayoutDashboard,
  Users,
  Upload,
  Calculator,
  Settings,
  FileText,
  BarChart3,
  Sliders,
  type LucideIcon,
} from 'lucide-react'

interface NavItem {
  label: string
  href: string
  icon: LucideIcon
}

interface SidebarProps {
  role: 'admin' | 'member'
  cerName?: string
  cerLogo?: string | null
}

const adminNavItems: NavItem[] = [
  { label: 'Dashboard', href: '/admin', icon: LayoutDashboard },
  { label: 'Configurazioni', href: '/admin/configurations', icon: Sliders },
  { label: 'Membri', href: '/admin/members', icon: Users },
  { label: 'Upload Dati', href: '/admin/upload', icon: Upload },
  { label: 'Calcolo TI', href: '/admin/calculations', icon: Calculator },
  { label: 'Report', href: '/admin/reports', icon: FileText },
  { label: 'Impostazioni', href: '/admin/settings', icon: Settings },
]

const memberNavItems: NavItem[] = [
  { label: 'Dashboard', href: '/member', icon: LayoutDashboard },
  { label: 'Guadagni', href: '/member/earnings', icon: BarChart3 },
  { label: 'Energia', href: '/member/energy', icon: Calculator },
  { label: 'Documenti', href: '/member/documents', icon: FileText },
]

export function Sidebar({ role, cerName = 'CER', cerLogo }: SidebarProps) {
  const navItems = role === 'admin' ? adminNavItems : memberNavItems

  return (
    <aside className="flex h-screen w-64 flex-col border-r border-[var(--color-border)] bg-[var(--color-bg-primary)]">
      {/* Logo/Brand */}
      <div className="flex h-16 items-center gap-3 border-b border-[var(--color-border)] px-4">
        {cerLogo ? (
          <img src={cerLogo} alt={cerName} className="h-8 w-auto" />
        ) : (
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--color-primary)] text-white font-bold">
            {cerName.charAt(0)}
          </div>
        )}
        <span className="font-semibold text-[var(--color-text-primary)]">{cerName}</span>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-4">
        <ul className="space-y-1">
          {navItems.map((item) => (
            <li key={item.href}>
              <NavLink
                to={item.href}
                end={item.href === '/admin' || item.href === '/member'}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-[var(--color-accent)] bg-opacity-10 text-[var(--color-accent)]'
                      : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)]'
                  )
                }
              >
                <item.icon className="h-5 w-5" />
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* Footer */}
      <div className="border-t border-[var(--color-border)] p-4">
        <p className="text-xs text-[var(--color-text-tertiary)]">
          {role === 'admin' ? 'Area Amministrazione' : 'Area Membri'}
        </p>
      </div>
    </aside>
  )
}

export default Sidebar
